import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    checkmarkColor = "checkmarkColor",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    imageBorder = "imageBorder",
    imageBorderColor = "imageBorderColor",
    imageBorderWidth = "imageBorderWidth",
    disableButton = "disableButton",
    bottomContentPadding = "bottomContentPadding",
    topContentPadding = "topContentPadding",
    contentAlignment = "contentAlignment",
    rightContentPadding = "rightContentPadding",
    leftContentPadding = "leftContentPadding",
    entireBannerLink = "entireBannerLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: number;
        [fields.checkmarkColor]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.imageBorder]: boolean;
        [fields.imageBorderColor]: string;
        [fields.imageBorderWidth]: number;
        [fields.disableButton]: boolean;
        [fields.bottomContentPadding]: number;
        [fields.topContentPadding]: number;
        [fields.rightContentPadding]: number;
        [fields.leftContentPadding]: number;
        [fields.contentAlignment]: "left" | "right";
        [fields.entireBannerLink]: LinkFieldValue;
    };
    extendedStyles?: PromotionalTileBannerStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.buttonLink);
    const bannerLink = getLink(state, ownProps.fields.entireBannerLink);
    return {
        bannerUrl: bannerLink?.url,
        url: link?.url,
        title: fields.buttonLabel,
    };
};

export interface PromotionalTileBannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    childWrapperStyles: InjectableCss;
}

export const bannerStyles: PromotionalTileBannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
    childWrapperStyles: {
        css: css`
            max-width: 1400px;
            padding: 0 17px;
            margin: auto;
            width: 100%;
        `,
    },
};

const onClick = (history: History, link: string | undefined) => {
    if (link) {
        history.push(link);
    }
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

const PromotionalTileBanner: React.FC<Props> = ({ fields, url, title, bannerUrl, history, extendedStyles }) => {
    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${fields.image});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    const minimumHeightStyles = `min-height: ${fields.minimumHeight}px;`;

    let topBottomOverlayPositioningStyles;
    if (!fields.partialOverlay) {
        topBottomOverlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        topBottomOverlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        topBottomOverlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        topBottomOverlayPositioningStyles = "align-items: flex-end;";
    }

    const [styles] = React.useState(() => mergeToNew(bannerStyles, extendedStyles));

    const borderStyle = fields.imageBorder
        ? `border: ${fields.imageBorderColor}; border-style: solid; border-width: ${fields.imageBorderWidth}px;`
        : "";

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${topBottomOverlayPositioningStyles}
            ${borderStyle}
            border-radius: 15px;
        `,
    };

    let flexTileOverlayPositioningStyles;
    if (fields.contentAlignment === "left") {
        flexTileOverlayPositioningStyles = "justify-content: flex-start;";
    } else if (fields.contentAlignment === "right") {
        flexTileOverlayPositioningStyles = "justify-content: flex-end;";
    } else {
        flexTileOverlayPositioningStyles = "justify-content: center;";
    }

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.topContentPadding}% ${fields.rightContentPadding}% ${fields.bottomContentPadding}%
                ${fields.leftContentPadding}%;
            text-align: ${fields.contentAlignment};
            ${flexTileOverlayPositioningStyles}
        `,
    };

    let checkmarkPositioningStyles;
    let listItemPadding;
    if (fields.contentAlignment === "left") {
        checkmarkPositioningStyles = "left: 1em;";
        listItemPadding = "padding: 0.25em 0 0 3em;";
    } else if (fields.contentAlignment === "right") {
        checkmarkPositioningStyles = "left: auto; right: 1em;";
        listItemPadding = "padding: 0.25em 3em 0 0;";
    } else {
        checkmarkPositioningStyles = "left: 1em;";
        listItemPadding = "padding: 0.25em 3em 0 3em;";
    }

    const checkmarkStyles = {
        css: css`
            ul li::after {
                content: " ";
                display: block;
                width: 0.6em;
                height: 1.1em;
                border: solid ${fields.checkmarkColor};
                border-width: 0 0.2em 0.2em 0;
                position: absolute;
                ${checkmarkPositioningStyles}
                top: 22%;
                margin-top: -0.2em;

                /* -webkit-transform: rotate(45deg);
                -moz-transform: rotate(45deg);
                -o-transform: rotate(45deg); */
                transform: rotate(45deg);
            }
            ul li {
                font-size: 15px;
                margin-bottom: 0.5em;
                list-style-type: none;
                ${listItemPadding}
                position: relative;
                text-align: ${fields.contentAlignment};
            }
            ul {
                list-style-type: none;
                padding: 0;
            }
            ${breakpointMediaQueries(baseTheme, [
                css`
                    ul li {
                        font-size: 15px;
                        margin-bottom: 0.5em;
                        list-style-type: none;
                        padding: 0.25em 0 0 3em;
                        position: relative;
                        flex: 0 0 50%;
                    }
                `,
                css`
                    ul li {
                        font-size: 15px;
                        margin-bottom: 0.5em;
                        list-style-type: none;
                        padding: 0.25em 0 0 3em;
                        position: relative;
                        flex: 0 0 50%;
                    }
                `,
                css`
                    margin-left: 0;
                    margin-right: 0;
                `,
                css`
                    margin-left: 0;
                    margin-right: 0;
                `,
                css`
                    margin-left: 0;
                    margin-right: 0;
                `,
            ])}
        `,
    };

    let showLink;
    if (!fields.entireBannerLink || fields.entireBannerLink.value === "") {
        showLink = false;
    } else {
        showLink = true;
    }

    const innerContent = (
        <StyledWrapper {...styles.childWrapperStyles} className="content_promotional_banner_child">
            <StyledWrapper className="content_promotional_banner_parent" {...overlayWrapperStyles}>
                <Typography className="content_promotional_banner_heading">
                    {parse(fields.heading, parserOptions)}
                </Typography>
                <StyledWrapper className="content_promotional_banner_subheading" {...checkmarkStyles}>
                    {parse(fields.subheading, parserOptions)}
                </StyledWrapper>
                {!fields.disableButton && (
                    <Button
                        className="content_promotional_banner_button"
                        {...styles.bannerButton}
                        variant={fields.variant}
                        onClick={() => onClick(history, url)}
                    >
                        {fields.buttonLabel || title || url}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <>
            {showLink ? (
                <Link {...wrapperStyles} className="content_promotional_banner" href={bannerUrl}>
                    {innerContent}
                </Link>
            ) : (
                <StyledWrapper {...wrapperStyles} className="content_promotional_banner">
                    {innerContent}
                </StyledWrapper>
            )}
        </>
    );
};

const promotionalTileBanner: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(PromotionalTileBanner)),
    definition: {
        group: "Basic",
        icon: "Banner",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.entireBannerLink,
                displayName: "Entire Banner Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
            },
            {
                fieldType: "General",
                name: fields.imageBorder,
                displayName: "Image Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.imageBorderColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.imageBorder,
            },
            {
                fieldType: "General",
                name: fields.imageBorderWidth,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tooltip: "Thickness of border in pixels",
                isVisible: widget => widget.fields.imageBorder,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "white",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                fieldType: "General",
                name: fields.checkmarkColor,
                editorTemplate: "ColorPickerField",
                displayName: "Checkmark Color",
                defaultValue: "black",
            },
            {
                name: fields.subheading,
                displayName: "Sub Heading Text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Delimited list of bullet points.",
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
            },
            {
                fieldType: "General",
                name: fields.topContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                tooltip: "Percentage padding value",
            },
            {
                fieldType: "General",
                name: fields.rightContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tooltip: "Percentage padding value",
            },
            {
                fieldType: "General",
                name: fields.bottomContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                tooltip: "Percentage padding value",
            },
            {
                fieldType: "General",
                name: fields.leftContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tooltip: "Percentage padding value",
            },
            {
                name: fields.contentAlignment,
                displayName: "Content Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                    { displayName: "Center", value: "center" },
                ],
                hideEmptyOption: true,
                defaultValue: "left",
                fieldType: "General",
            },
        ],
    },
};

export default promotionalTileBanner;
